/**
 * WeeklyActivityChart
 *
 * 연간 활동 시각화 차트 (SVG 기반)
 *
 * [Week View - 기본]
 * - X축: 52주 타임라인
 * - Y축: 중앙 정렬
 * - 각 원 = 주간 집계
 * - 원 크기 = 총 조회수, 원 투명도 = 가중 참여율
 * - 주 클릭 시 Thread View로 전환
 *
 * [Thread View - 주 클릭 시]
 * - X축: 요일 (월-일)
 * - Y축: 시간대 (0-24시)
 * - 각 원 = 개별 스레드
 * - 원 크기 = 조회수, 원 투명도 = 가중 참여율
 */

import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ThreadTooltip, ActivityTooltip } from './activity';
import { getCircleRadius, getCircleOpacity, CIRCLE_CONFIG } from '../../utils/activityCalculator';

/**
 * 차트 설정
 */
const CHART_CONFIG = {
  viewBoxWidth: 900,
  viewBoxHeight: 280,
  padding: { top: 24, right: 24, bottom: 40, left: 40 },
};

const MONTH_NAMES_KO = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
const MONTH_NAMES_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_NAMES_KO = ['월', '화', '수', '목', '금', '토', '일'];
const DAY_NAMES_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Week View - 하단 X축 (월 레이블)
 */
const MonthAxis = ({ monthRanges, xScale, yPosition, theme, isMobile, isKorean }) => {
  const monthNames = isKorean ? MONTH_NAMES_KO : MONTH_NAMES_EN;
  const visibleMonths = isMobile
    ? monthRanges.filter((_, i) => i % 2 === 0)
    : monthRanges;

  return (
    <g className="month-axis">
      <line
        x1={0}
        y1={yPosition}
        x2={xScale(52)}
        y2={yPosition}
        stroke={theme.palette.divider}
        strokeWidth={1}
        opacity={0.4}
      />
      {monthRanges.map((range, index) => {
        if (index === 0) return null;
        const x = xScale(range.startWeek);
        return (
          <line
            key={`tick-${index}`}
            x1={x}
            y1={yPosition - 4}
            x2={x}
            y2={yPosition + 4}
            stroke={theme.palette.divider}
            strokeWidth={1}
            opacity={0.5}
          />
        );
      })}
      {visibleMonths.map((range) => {
        const centerWeek = (range.startWeek + range.endWeek) / 2;
        const x = xScale(centerWeek);
        return (
          <text
            key={`label-${range.month}`}
            x={x}
            y={yPosition + 18}
            textAnchor="middle"
            fill={theme.palette.text.secondary}
            fontSize={isMobile ? 9 : 10}
            fontWeight={500}
            fontFamily={theme.typography.fontFamily}
          >
            {monthNames[range.month]}
          </text>
        );
      })}
    </g>
  );
};

/**
 * Thread View - 하단 X축 (요일 레이블)
 */
const DayAxis = ({ xScale, yPosition, theme, isKorean }) => {
  const dayNames = isKorean ? DAY_NAMES_KO : DAY_NAMES_EN;

  return (
    <g className="day-axis">
      <line
        x1={xScale(0)}
        y1={yPosition}
        x2={xScale(6)}
        y2={yPosition}
        stroke={theme.palette.divider}
        strokeWidth={1}
        opacity={0.4}
      />
      {[0, 1, 2, 3, 4, 5, 6].map((day) => (
        <text
          key={`day-${day}`}
          x={xScale(day)}
          y={yPosition + 18}
          textAnchor="middle"
          fill={theme.palette.text.secondary}
          fontSize={10}
          fontWeight={500}
          fontFamily={theme.typography.fontFamily}
        >
          {dayNames[day]}
        </text>
      ))}
    </g>
  );
};

/**
 * Thread View - 좌측 Y축 (시간대 레이블)
 */
const TimeAxis = ({ yScale, xPosition, theme }) => {
  const timeLabels = [0, 6, 12, 18, 24];

  return (
    <g className="time-axis">
      <line
        x1={xPosition}
        y1={yScale(0)}
        x2={xPosition}
        y2={yScale(24)}
        stroke={theme.palette.divider}
        strokeWidth={1}
        opacity={0.4}
      />
      {timeLabels.map((hour) => {
        const y = yScale(hour);
        return (
          <g key={`time-${hour}`}>
            <line
              x1={xPosition - 4}
              y1={y}
              x2={xPosition + 4}
              y2={y}
              stroke={theme.palette.divider}
              strokeWidth={1}
              opacity={0.5}
            />
            <text
              x={xPosition - 8}
              y={y + 3}
              textAnchor="end"
              fill={theme.palette.text.secondary}
              fontSize={9}
              fontFamily={theme.typography.fontFamily}
            >
              {hour}h
            </text>
          </g>
        );
      })}
    </g>
  );
};

/**
 * Thread View - 그리드 라인
 */
const TimeGridLines = ({ xScale, yScale, innerWidth, theme }) => {
  const timeLines = [6, 12, 18];

  return (
    <g className="grid-lines" opacity={0.1}>
      {timeLines.map((hour) => (
        <line
          key={`grid-h-${hour}`}
          x1={0}
          y1={yScale(hour)}
          x2={innerWidth}
          y2={yScale(hour)}
          stroke={theme.palette.divider}
          strokeWidth={1}
          strokeDasharray="4,4"
        />
      ))}
    </g>
  );
};

/**
 * Week View - 주간 집계 원
 */
const WeekCircle = ({ week, cx, cy, isHovered, theme, onClick }) => {
  const radius = getCircleRadius(week.normalizedViews);
  const opacity = getCircleOpacity(week.normalizedEngagement);

  return (
    <g style={{ cursor: week.threadCount > 0 ? 'pointer' : 'default' }}>
      <motion.circle
        cx={cx}
        cy={cy}
        r={radius}
        fill={theme.palette.primary.main}
        stroke={theme.palette.background.paper}
        strokeWidth={isHovered ? CIRCLE_CONFIG.strokeWidth + 1.5 : CIRCLE_CONFIG.strokeWidth}
        initial={{ r: 0, opacity: 0 }}
        animate={{ r: radius, opacity: isHovered ? Math.min(opacity + 0.2, 1) : opacity }}
        transition={{
          r: { duration: 0.3, delay: week.weekNumber * 0.003, ease: 'easeOut' },
          opacity: { duration: 0.1 },
        }}
        style={{ pointerEvents: 'none' }}
      />
      {/* Hit Area */}
      <circle
        cx={cx}
        cy={cy}
        r={Math.max(radius + 8, 16)}
        fill="transparent"
        onClick={() => week.threadCount > 0 && onClick(week)}
      />
      {/* 중심점 (hover 시) */}
      {isHovered && (
        <circle
          cx={cx}
          cy={cy}
          r={3}
          fill={theme.palette.background.paper}
          style={{ pointerEvents: 'none' }}
        />
      )}
    </g>
  );
};

/**
 * Thread View - 개별 스레드 원
 */
const ThreadDot = ({ thread, cx, cy, originX, originY, isHovered, theme, index }) => {
  const radius = getCircleRadius(thread.normalizedViews);
  const opacity = getCircleOpacity(thread.normalizedEngagement);

  return (
    <motion.circle
      cx={cx}
      cy={cy}
      r={radius}
      fill={theme.palette.primary.main}
      stroke={theme.palette.background.paper}
      strokeWidth={isHovered ? CIRCLE_CONFIG.strokeWidth + 1.5 : CIRCLE_CONFIG.strokeWidth}
      initial={{ cx: originX, cy: originY, r: 0, opacity: 0 }}
      animate={{
        cx,
        cy,
        r: radius,
        opacity: isHovered ? Math.min(opacity + 0.2, 1) : opacity
      }}
      exit={{ cx: originX, cy: originY, r: 0, opacity: 0 }}
      transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: index * 0.008,
      }}
      style={{ pointerEvents: 'none' }}
    />
  );
};

/**
 * Thread View - Hit Area
 */
const ThreadHitArea = ({ cx, cy, radius, thread, onHover, onLeave, onClick }) => (
  <circle
    cx={cx}
    cy={cy}
    r={Math.max(radius + 6, 12)}
    fill="transparent"
    style={{ cursor: 'pointer' }}
    onMouseEnter={(e) => onHover(thread, { x: cx, y: cy }, e.nativeEvent)}
    onMouseMove={(e) => onHover(thread, { x: cx, y: cy }, e.nativeEvent)}
    onMouseLeave={onLeave}
    onClick={() => onClick(thread)}
  />
);

/**
 * WeeklyActivityChart 메인 컴포넌트
 */
const WeeklyActivityChart = ({ activitySummary }) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const containerRef = useRef(null);
  const isMobile = useIsMobile('md');
  const isKorean = i18n.language === 'ko';

  // 뷰 상태
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'thread'
  const [selectedWeek, setSelectedWeek] = useState(null);

  // 호버 상태
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);

  // 차트 설정
  const { viewBoxWidth, viewBoxHeight, padding } = CHART_CONFIG;
  const innerWidth = viewBoxWidth - padding.left - padding.right;
  const innerHeight = viewBoxHeight - padding.top - padding.bottom;

  // Week View 스케일
  const weekXScale = useMemo(() => {
    return d3.scaleLinear()
      .domain([1, 52])
      .range([0, innerWidth]);
  }, [innerWidth]);

  const weekYCenter = innerHeight / 2;

  // Thread View 스케일
  const threadXScale = useMemo(() => {
    return d3.scaleLinear()
      .domain([0, 6])
      .range([40, innerWidth - 40]);
  }, [innerWidth]);

  const threadYScale = useMemo(() => {
    return d3.scaleLinear()
      .domain([0, 24])
      .range([0, innerHeight]);
  }, [innerHeight]);

  // 선택된 주의 원 중심 좌표 (애니메이션 origin)
  const selectedWeekOrigin = useMemo(() => {
    if (!selectedWeek) return { x: innerWidth / 2, y: weekYCenter };
    return {
      x: weekXScale(selectedWeek.weekNumber),
      y: weekYCenter,
    };
  }, [selectedWeek, weekXScale, weekYCenter, innerWidth]);

  // 주 클릭 핸들러
  const handleWeekClick = useCallback((week) => {
    if (week.threads && week.threads.length > 0) {
      setSelectedWeek(week);
      setViewMode('thread');
      setHoveredItem(null);
      setTooltipPosition(null);
    }
  }, []);

  // 배경 클릭 → Week View로 복귀
  const handleBackgroundClick = useCallback(() => {
    if (viewMode === 'thread') {
      setViewMode('week');
      setSelectedWeek(null);
      setHoveredItem(null);
      setTooltipPosition(null);
    }
  }, [viewMode]);

  // 호버 핸들러
  const handleThreadHover = useCallback((thread, position, event) => {
    setHoveredItem(thread);
    if (containerRef.current && event) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    }
  }, []);

  const handleThreadLeave = useCallback(() => {
    setHoveredItem(null);
    setTooltipPosition(null);
  }, []);

  // 스레드 클릭 → 새 탭에서 열기
  const handleThreadClick = useCallback((thread) => {
    if (thread.permalink) {
      window.open(thread.permalink, '_blank', 'noopener,noreferrer');
    }
  }, []);

  if (!activitySummary || !activitySummary.weeks) {
    return null;
  }

  const activeWeeks = activitySummary.weeks.filter(w => w.threadCount > 0);

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', width: '100%' }}
    >
      <motion.svg
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: '100%',
          height: 'auto',
          overflow: 'visible',
          display: 'block',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <g transform={`translate(${padding.left}, ${padding.top})`}>
          {/* 배경 클릭 영역 */}
          <rect
            x={-padding.left}
            y={-padding.top}
            width={viewBoxWidth}
            height={viewBoxHeight}
            fill="transparent"
            onClick={handleBackgroundClick}
          />

          <AnimatePresence mode="wait">
            {viewMode === 'week' ? (
              /* ========== WEEK VIEW ========== */
              <motion.g
                key="week-view"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* X축 - 월 */}
                <MonthAxis
                  monthRanges={activitySummary.monthRanges}
                  xScale={weekXScale}
                  yPosition={innerHeight}
                  theme={theme}
                  isMobile={isMobile}
                  isKorean={isKorean}
                />

                {/* 주간 집계 원들 */}
                {activeWeeks.map((week) => (
                  <WeekCircle
                    key={week.weekNumber}
                    week={week}
                    cx={weekXScale(week.weekNumber)}
                    cy={weekYCenter}
                    isHovered={hoveredItem?.weekNumber === week.weekNumber && viewMode === 'week'}
                    theme={theme}
                    onClick={handleWeekClick}
                  />
                ))}

                {/* Week 호버용 Hit Areas */}
                {activeWeeks.map((week) => (
                  <circle
                    key={`hover-${week.weekNumber}`}
                    cx={weekXScale(week.weekNumber)}
                    cy={weekYCenter}
                    r={Math.max(getCircleRadius(week.normalizedViews) + 8, 16)}
                    fill="transparent"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => {
                      setHoveredItem(week);
                      if (containerRef.current) {
                        const rect = containerRef.current.getBoundingClientRect();
                        setTooltipPosition({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                        });
                      }
                    }}
                    onMouseMove={(e) => {
                      if (containerRef.current) {
                        const rect = containerRef.current.getBoundingClientRect();
                        setTooltipPosition({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                        });
                      }
                    }}
                    onMouseLeave={() => {
                      setHoveredItem(null);
                      setTooltipPosition(null);
                    }}
                    onClick={() => handleWeekClick(week)}
                  />
                ))}
              </motion.g>
            ) : (
              /* ========== THREAD VIEW ========== */
              <motion.g
                key="thread-view"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* 그리드 라인 */}
                <TimeGridLines
                  xScale={threadXScale}
                  yScale={threadYScale}
                  innerWidth={innerWidth}
                  theme={theme}
                />

                {/* Y축 - 시간대 */}
                <TimeAxis
                  yScale={threadYScale}
                  xPosition={0}
                  theme={theme}
                />

                {/* X축 - 요일 */}
                <DayAxis
                  xScale={threadXScale}
                  yPosition={innerHeight}
                  theme={theme}
                  isKorean={isKorean}
                />

                {/* 개별 스레드 원들 */}
                {selectedWeek?.threads.map((thread, index) => (
                  <ThreadDot
                    key={thread.id}
                    thread={thread}
                    cx={threadXScale(thread.dayOfWeek)}
                    cy={threadYScale(thread.hourOfDay)}
                    originX={selectedWeekOrigin.x}
                    originY={selectedWeekOrigin.y}
                    index={index}
                    isHovered={hoveredItem?.id === thread.id}
                    theme={theme}
                  />
                ))}

                {/* Thread Hit Areas */}
                {selectedWeek?.threads.map((thread) => (
                  <ThreadHitArea
                    key={`hit-${thread.id}`}
                    cx={threadXScale(thread.dayOfWeek)}
                    cy={threadYScale(thread.hourOfDay)}
                    onClick={handleThreadClick}
                    radius={getCircleRadius(thread.normalizedViews)}
                    thread={thread}
                    onHover={handleThreadHover}
                    onLeave={handleThreadLeave}
                  />
                ))}

                {/* 뒤로가기 힌트 */}
                <text
                  x={innerWidth}
                  y={-8}
                  textAnchor="end"
                  fill={theme.palette.text.secondary}
                  fontSize={10}
                  fontFamily={theme.typography.fontFamily}
                  style={{ cursor: 'pointer' }}
                  onClick={handleBackgroundClick}
                >
                  {isKorean ? '← 전체 보기' : '← Back to overview'}
                </text>
              </motion.g>
            )}
          </AnimatePresence>
        </g>
      </motion.svg>

      {/* Week View 툴팁 */}
      {hoveredItem && tooltipPosition && viewMode === 'week' && (
        <ActivityTooltip
          week={hoveredItem}
          position={tooltipPosition}
        />
      )}

      {/* Thread View 툴팁 */}
      {hoveredItem && tooltipPosition && viewMode === 'thread' && (
        <ThreadTooltip
          thread={hoveredItem}
          position={tooltipPosition}
        />
      )}
    </Box>
  );
};

export default WeeklyActivityChart;
